import type { TrackEventRequest } from "@b2bportal/event-tracking-api";
import { trackEvent } from "@hopper-b2b/api";

/**
 * Destination for tracking fired from checkout machine code. Receives the
 * request exactly as the machine built it (name, properties, encrypted
 * properties); the sink decides where it goes. May be sync or async.
 */
export type CheckoutTrackingSink = (
  request: TrackEventRequest
) => void | Promise<unknown>;

const defaultSink: CheckoutTrackingSink = (request) => trackEvent(request);

let sink: CheckoutTrackingSink = defaultSink;

/**
 * Tracking entry point for checkout machine code (actions and invoked
 * services). Machine code runs outside React and, for services, outside XState
 * transitions, so a module-level seam is the only place a tenant can plug its
 * own tracking pipeline (extra sinks, enrichment). Defaults to `trackEvent`.
 *
 * Always resolves to a promise: a sink that throws synchronously becomes a
 * rejection instead of breaking the machine action that fired the event.
 *
 * @param request Event to track, as built by the machine.
 * @returns Whatever the active sink returns, wrapped in a promise.
 */
export const trackCheckoutEvent = (
  request: TrackEventRequest
): Promise<unknown> => new Promise((resolve) => resolve(sink(request)));

/**
 * Replaces the sink used by `trackCheckoutEvent` for the whole app.
 *
 * Call it once from the tenant's tracking provider (in an effect) and call the
 * returned function on unmount so a stale closure never receives events. The
 * reset restores whatever sink was active before, so nested registrations
 * unwind cleanly.
 *
 * @param next Sink that receives every machine-fired event from now on.
 * @returns Function that restores the previously active sink.
 */
export const setCheckoutTrackingSink = (next: CheckoutTrackingSink) => {
  const previous = sink;
  sink = next;
  return () => {
    sink = previous;
  };
};
